import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';
import { companyFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import {
  isFacebookUserSelector,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { useMachine } from '@xstate/react';
import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Machine } from 'xstate';
import { SIGNUP } from '../../../../../common/helpers/plugins/constants';
import { trackFacebookEvent } from '../../../../../common/helpers/plugins/facebook-pixel-init';
import { trackSignUpEventGA } from '../../../../../modules/MarketingAnalytics';
import {
  LEAD_LISTS_LOOKUP,
  TEAM_SIZE_LOOKUP,
  WEBSITE_VISITORS_LOOKUP,
} from '../../../../signUpFlowMachine/helpers';
import { signUpFlowMachineConfig } from '../../../../signUpFlowMachine/signUpFlowMachine';
import {
  SignUpFlowEvents,
  SignUpFlowEventsTypes,
} from '../../../../signUpFlowMachine/signUpMachineEventTypes';
import {
  SignUpFlowContext,
  SignUpFlowStatesSchema,
  SIGN_UP_FLOW_STATES_NAME,
} from '../../../../signUpFlowMachine/signUpMachineTypes';
import OnboardingPageLoadingIndicator from '../../common/OnboardingPageLoadingIndicator/OnboardingPageLoadingIndicator';
import { useSetNextOnboardingStep } from '../../use-set-next-onboarding-step';
import { SetCompanyInfo } from './components/SetCompanyInfo/SetCompanyInfo';
import { WelcomePage } from './components/WelcomePage/WelcomePage';
import './NewUserInfoFlow.scss';

export const NewUserInfoFlow = () => {
  const activeCompany = useSelector(activeCompanySelector);
  const isFBUser = useSelector(isFacebookUserSelector);
  const isNewSignupFlow = useSelector(
    companyFeatureFlagsSelector,
  ).onboarding2022;

  const { update } = useCompanyCRUDActions();
  const { setNextOnboardingStep } = useSetNextOnboardingStep();
  const { userAttributes, id: userId } = useSelector(selectSessionSelector);
  const { updateUserAttributes } = useUsersCRUDActions();

  const redirectService = useCallback(async (ctx: SignUpFlowContext) => {
    if (ctx.company?.id) {
      await update({
        id: ctx.company.id,
        goal: ctx.company.goal,
      });
    }
    trackFacebookEvent(SIGNUP);
    trackSignUpEventGA();
    setNextOnboardingStep(
      OnboardingSignupStep.NONE,
      false,
      isFBUser ? 'facebook' : undefined,
    );
    return null;
  }, []);

  const updateCompanyService = useCallback(async (ctx: SignUpFlowContext) => {
    if (ctx.company && ctx.company?.id) {
      const {
        name,
        businessEmail,
        website,
        phoneNumber,
        jobRole,
        clientsCharge,
        monthlyAdBudget,
        industry,
        websiteVisitors,
        leadsList,
        goal,
      } = ctx.company;
      const { employeeCount } = ctx.company;
      const countObject = employeeCount
        ? {
            employeeCount:
              TEAM_SIZE_LOOKUP[employeeCount as keyof typeof TEAM_SIZE_LOOKUP],
          }
        : {};
      update({
        id: ctx.company.id,
        name,
        businessEmail,
        website,
        phoneNumber,
        jobRole,
        employeeCount,
        clientsCharge,
        monthlyAdBudget,
        industry,
        goal,
        websiteVisitors:
          WEBSITE_VISITORS_LOOKUP[
            websiteVisitors as keyof typeof WEBSITE_VISITORS_LOOKUP
          ],
        leadsList:
          LEAD_LISTS_LOOKUP[leadsList as keyof typeof LEAD_LISTS_LOOKUP],
        ...countObject,
      });

      if (ctx.company.id) {
        updateUserAttributes(ctx.company.id, userId, {
          onboardingCompanyInfoStep: ctx.activeStep,
        });
      }
    }
    return null;
  }, []);

  const signUpFlowOptions = useMemo(() => {
    return {
      services: {
        updateCompanyService,
        redirectService,
      },
    };
  }, []);

  const signUpFlowMachine = useMemo(() => {
    return Machine<SignUpFlowContext, SignUpFlowStatesSchema, SignUpFlowEvents>(
      signUpFlowMachineConfig,
      signUpFlowOptions,
    );
  }, [signUpFlowOptions]);

  const [current, send, service] = useMachine(signUpFlowMachine, {
    context: {
      company: activeCompany,
      isFBUser,
      isNewSignupFlow,
      activeStep: userAttributes.onboardingCompanyInfoStep ?? 0,
    },
    devTools: false,
  });

  const { activeStep, company } = current.context;

  const isWelcomeScreenActive = current.matches(
    SIGN_UP_FLOW_STATES_NAME.WELCOME,
  );
  const isVideoScreenActive = current.matches(SIGN_UP_FLOW_STATES_NAME.VIDEO);
  const isConnectChannelsStep = current.matches(
    SIGN_UP_FLOW_STATES_NAME.CONNECT_CHANNELS,
  );
  const isRedirect = current.matches(SIGN_UP_FLOW_STATES_NAME.REDIRECT);

  const sendStartEvent = (
    name: string,
    businessEmail: string,
    website: string,
    phoneNumber: string,
  ) => {
    send({
      type: SignUpFlowEventsTypes.START,
      data: {
        name,
        businessEmail,
        website,
        phoneNumber,
      },
    });
  };

  const sendGoBackEvent = () => {
    send(SignUpFlowEventsTypes.GO_BACK);
  };

  if (!company) {
    return null;
  }

  const headerClassname = classNames({
    'user-info-flow__header': !isVideoScreenActive,
    'user-info-flow__welcome-page-header': isWelcomeScreenActive,
    'user-info-flow__header--video':
      isVideoScreenActive || isConnectChannelsStep,
  });

  if (isRedirect) {
    return <></>;
  }

  return (
    <div className="user-info-flow">
      {!isWelcomeScreenActive && <div className={headerClassname} />}
      {isWelcomeScreenActive ? (
        <WelcomePage onStartPress={sendStartEvent} company={company} />
      ) : (
        <SetCompanyInfo
          goBack={sendGoBackEvent}
          activeStep={activeStep}
          service={service}
          isVideoScreenActive={isVideoScreenActive}
          isConnectChannelsStep={isConnectChannelsStep}
        />
      )}

      <OnboardingPageLoadingIndicator isLoading={false} />
    </div>
  );
};
